import { type ReactNode, useId } from "react";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { Picker } from "@design-system/Picker/Picker.lib";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";

interface BoardDisplaySelectProps {
    label: string;
    items: ReadonlyArray<PickerItemData<string>>;
    value: string;
    onValueChange: (value: string) => void;
    /** Turns the picker off and shows this under it. */
    disabledReason?: string;
    /** A control beside the picker, such as the sort direction. */
    trailing?: ReactNode;
    /** A note under the row about `trailing`; `id` lets it describe that control. */
    trailingHint?: { id: string; text: string };
}

/**
 * One Display panel row: its label at the start names the picker at
 * the end, with `trailing` beside the picker and any reasons under the row.
 */
export const BoardDisplaySelect = ({
    label,
    items,
    value,
    onValueChange,
    disabledReason,
    trailing,
    trailingHint,
}: Readonly<BoardDisplaySelectProps>) => {
    const labelId = useId();
    const reasonId = useId();
    const hasHint = disabledReason !== undefined || trailingHint !== undefined;

    return (
        <SettingsPanel.Row
            label={label}
            labelId={labelId}
            hint={
                hasHint ? (
                    <>
                        {disabledReason !== undefined && (
                            <div id={reasonId}>{disabledReason}</div>
                        )}
                        {trailingHint !== undefined && (
                            <div id={trailingHint.id}>{trailingHint.text}</div>
                        )}
                    </>
                ) : undefined
            }
        >
            <Picker.Select
                items={items}
                value={value}
                onValueChange={(next) => {
                    if (next !== null) {
                        onValueChange(next);
                    }
                }}
                disabled={disabledReason !== undefined}
                chevron="none"
                trigger={
                    <Picker.Button
                        variant="primary"
                        size="sm"
                        aria-labelledby={labelId}
                        aria-describedby={
                            disabledReason === undefined ? undefined : reasonId
                        }
                    />
                }
            />
            {trailing}
        </SettingsPanel.Row>
    );
};
